/**
 * 页脚备案组件：版权 + ICP / 公安备案（可选图标）。
 * 全站统一槽位：#siteFooter（写在各页 .shell 内同一位置），由 motion 入口挂载一次。
 * 数据来自 GET /api/system/config/beian；失败则隐藏槽位。
 */
import { getBeianConfig } from '@/api/system';
import type { BeianConfig } from '@/types/system';
import { esc } from '@/utils/escape';

const FOOTER_ID = 'siteFooter';

function resolvePoliceHref(policeNo: string, policeLink: string): string {
  const link = String(policeLink || '').trim();
  if (!link) return '#';
  const digits = String(policeNo).replace(/\D/g, '');
  if (!digits) return link;
  if (link.includes('recordcode=')) return link;
  const join = link.includes('?') ? '&' : '?';
  return `${link}${join}recordcode=${digits}`;
}

/** 仅当 src 非空时输出 img；onerror 自移除避免破图 */
function beianIconHtml(src: string, alt: string): string {
  const url = String(src || '').trim();
  if (!url) return '';
  return `<img class="site-footer__icon" src="${esc(url)}" alt="${esc(alt)}" width="18" height="18" loading="lazy" decoding="async" onerror="this.remove()">`;
}

function beianLinkHtml(opts: {
  href: string;
  text: string;
  iconSrc: string;
  iconAlt: string;
}): string {
  const text = String(opts.text || '').trim();
  if (!text) return '';
  const href = String(opts.href || '').trim();
  if (!href) return '';
  const icon = beianIconHtml(opts.iconSrc, opts.iconAlt);
  return `<a class="site-footer__beian-link" href="${esc(href)}" target="_blank" rel="noopener noreferrer">${icon}<span class="site-footer__beian-text">${esc(text)}</span></a>`;
}

export function buildSiteFooterInnerHtml(cfg: BeianConfig): string {
  const copyright = String(cfg.copyrightText || '').trim();
  const icp = beianLinkHtml({
    href: cfg.icpLink,
    text: cfg.icpNo,
    iconSrc: cfg.icpIcon,
    iconAlt: 'ICP备案',
  });
  const police = beianLinkHtml({
    href: resolvePoliceHref(cfg.policeNo, cfg.policeLink),
    text: cfg.policeNo,
    iconSrc: cfg.policeIcon,
    iconAlt: '公安网安备案',
  });

  const beianRow =
    icp || police
      ? `<p class="site-footer__beian">${[icp, police].filter(Boolean).join('')}</p>`
      : '';

  if (!copyright && !beianRow) return '';

  return `${copyright ? `<p class="site-footer__copy">${esc(copyright)}</p>` : ''}${beianRow}`;
}

function getFooterSlot(): HTMLElement | null {
  return document.getElementById(FOOTER_ID);
}

function hideFooter(el: HTMLElement): void {
  el.hidden = true;
  el.setAttribute('aria-busy', 'false');
  el.classList.remove('site-footer--loading');
  el.innerHTML = '';
}

function showLoading(el: HTMLElement): void {
  el.hidden = false;
  el.setAttribute('aria-busy', 'true');
  el.classList.add('site-footer--loading');
  el.innerHTML =
    '<p class="site-footer__copy site-footer__placeholder" aria-hidden="true"></p>';
}

/**
 * 填充全站统一备案槽 #siteFooter（hub / play 共用，勿在页面内重复调用）。
 */
export async function mountSiteFooter(): Promise<void> {
  const el = getFooterSlot();
  if (!el) return;

  showLoading(el);

  try {
    const json = await getBeianConfig();
    if (!json.success || !json.data) {
      hideFooter(el);
      return;
    }
    const inner = buildSiteFooterInnerHtml(json.data as BeianConfig);
    if (!inner) {
      hideFooter(el);
      return;
    }
    el.classList.remove('site-footer--loading');
    el.setAttribute('aria-busy', 'false');
    el.hidden = false;
    el.innerHTML = inner;
  } catch {
    hideFooter(el);
  }
}
